/**
 * ModuleDetail.jsx — Volwaardige module-detailpagina template
 * Gebruik per module: website, SEO, reputatie, lokaal, advertenties, etc.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import usePlan from '../../hooks/usePlan';
import LockedSection from '../../components/LockedSection';
import ActionTasks from '../../components/ActionTasks';
import {
  CheckCircle, AlertCircle, ArrowRight, ShoppingCart, Lock, ChevronDown, ChevronUp, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const PRIORITY_BADGE = {
  hoog: 'bg-red-100 text-red-700',
  middel: 'bg-orange-100 text-orange-700',
  laag: 'bg-blue-100 text-blue-700',
};

export default function ModuleDetail({
  title, subtitle, icon: Icon, color = '#D4A017',
  analyse, score = 0, problems = [], solutions = [], services = [],
}) {
  const { api } = useAuth();
  const plan = usePlan();
  const isPaid = plan.isPaid;
  const isManaged = plan.isManaged;
  // Hide à-la-carte one-off services for managed users (they get this work done)
  const visibleServices = isManaged ? [] : services;
  const [ordering, setOrdering] = useState(null);
  const [openProblem, setOpenProblem] = useState(0);

  const handleOrder = async (productId, name) => {
    setOrdering(productId);
    try {
      await api('post', '/api/platform/orders', { product_id: productId });
      toast.success(`Bestelling geplaatst: ${name}. Wij nemen contact op!`);
    } catch { toast.error('Bestellen mislukt.'); }
    finally { setOrdering(null); }
  };

  // Score ring inline
  const r = 32, circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  const scoreColor = score >= 70 ? '#22c55e' : score >= 50 ? '#D4A017' : '#ef4444';

  return (
    <div className="max-w-4xl space-y-5">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm"
          style={{ background: `${color}15` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-[#0f172a]">{title}</h1>
          <p className="text-[#64748b] text-sm mt-0.5">{subtitle}</p>
        </div>
        {/* Score */}
        <div className="text-center flex-shrink-0">
          <svg width="80" height="80">
            <circle cx="40" cy="40" r={r} fill="none" stroke="#f1f5f9" strokeWidth="7" />
            <circle cx="40" cy="40" r={r} fill="none" stroke={scoreColor} strokeWidth="7"
              strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
              transform="rotate(-90 40 40)" />
            <text x="40" y="45" textAnchor="middle" fill={scoreColor} fontSize="17" fontWeight="700">{score}</text>
          </svg>
          <p className="text-[#64748b] text-[10px] -mt-1">score</p>
        </div>
      </div>

      {/* Problemen — toon Huidig → Beter waar beschikbaar */}
      {problems.length > 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <h2 className="font-bold text-[#0f172a] text-sm">Verbeterpunten ({problems.length})</h2>
          </div>
          <div className="space-y-2">
            {problems.map((p, i) => (
              <div key={i} className="border border-gray-100 rounded-xl overflow-hidden">
                <button onClick={() => setOpenProblem(openProblem === i ? null : i)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${PRIORITY_BADGE[p.priority || 'middel']} flex-shrink-0`}>
                      {p.priority || 'middel'}
                    </span>
                    <div className="min-w-0">
                      <p className="text-[#0f172a] font-semibold text-sm truncate">{p.title}</p>
                      {p.pagina && <p className="text-[10px] text-[#94a3b8] truncate">Pagina: {p.pagina}</p>}
                    </div>
                  </div>
                  {openProblem === i ? <ChevronUp size={14} className="text-[#94a3b8]" /> : <ChevronDown size={14} className="text-[#94a3b8]" />}
                </button>
                {openProblem === i && (
                  <div className="px-4 pb-4 border-t border-gray-50 bg-[#fafafa]">
                    {p.desc && <p className="text-[#64748b] text-sm mt-3 mb-3">{p.desc}</p>}
                    {/* Huidig → Beter blok als de scan dit heeft */}
                    {(p.huidige_situatie || p.beter_voorstel) && (
                      <div className="grid sm:grid-cols-2 gap-2 mb-3">
                        {p.huidige_situatie && (
                          <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                            <p className="text-[10px] font-bold text-red-700 uppercase tracking-wider mb-1">Huidig op uw site</p>
                            <p className="text-xs text-[#0f172a] italic leading-relaxed">"{p.huidige_situatie}"</p>
                          </div>
                        )}
                        {p.beter_voorstel && (
                          <div className="bg-[#fef9e7] border border-[#D4A017]/30 rounded-lg p-3">
                            <p className="text-[10px] font-bold text-[#92400e] uppercase tracking-wider mb-1">Beter voorstel</p>
                            <p className="text-xs text-[#0f172a] font-semibold leading-relaxed">{p.beter_voorstel}</p>
                          </div>
                        )}
                      </div>
                    )}
                    {p.impact && (
                      <div className="bg-green-50 border border-green-100 rounded-lg px-3 py-2 mb-3 text-xs text-green-700">
                        <span className="font-bold">Waarom beter:</span> {p.impact}
                      </div>
                    )}
                    {p.execution_mode && (
                      <p className="text-[10px] text-[#64748b] mb-3">
                        Mode: <span className="font-bold">{p.execution_mode}</span>
                        {plan.canExecute(p.execution_mode) ? ' · beschikbaar in uw plan' : ' · upgrade nodig'}
                      </p>
                    )}
                    {!isManaged && (
                      <div className="flex gap-2 flex-wrap">
                        <button className="text-xs font-bold text-[#0f172a] border border-gray-200 px-4 py-2 rounded-lg hover:bg-gray-50 transition-all">
                          Zelf doen
                        </button>
                        {p.service && (
                          <button onClick={() => handleOrder(p.service.id, p.service.name)}
                            disabled={ordering === p.service.id}
                            className="flex items-center gap-1.5 text-xs font-bold text-[#D4A017] border border-[#D4A017]/30 px-4 py-2 rounded-lg hover:bg-[#D4A017]/10 transition-all">
                            <ShoppingCart size={11} /> Door IMPERIA AI · {p.service.price}
                          </button>
                        )}
                      </div>
                    )}
                    {isManaged && (
                      <p className="text-xs text-[#16a34a] font-semibold">
                        ✓ Wordt automatisch opgepakt door uw Managed AI plan.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div data-testid="module-empty-state" className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
          <p className="text-sm font-semibold text-[#0f172a]">Nog geen verbeterpunten</p>
          <p className="text-xs text-[#64748b] mt-1">Voer eerst de scan uit om concrete Huidig → Beter aanbevelingen te ontvangen.</p>
        </div>
      )}

      {/* Oplossingen & Uitvoering (locked voor gratis) */}
      <LockedSection isPaid={isPaid}
        title="Volledige oplossingen beschikbaar"
        upgradeText="Ontgrendel stap-voor-stap instructies en uitvoeringsopties met IMPERIA AI Radar">
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <h2 className="font-bold text-[#0f172a] text-sm">Concrete oplossingen</h2>
          </div>
          <div className="space-y-3">
            {solutions.map((s, i) => (
              <div key={i} className="flex gap-3 p-3 bg-[#f8fafc] rounded-xl">
                <div className="w-5 h-5 bg-[#D4A017] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#0f172a] font-bold text-[10px]">{i + 1}</span>
                </div>
                <div>
                  <p className="text-[#0f172a] font-semibold text-sm">{s.title}</p>
                  <p className="text-[#64748b] text-xs mt-0.5">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </LockedSection>

      {/* Aanbevolen diensten (alleen voor non-managed) */}
      {visibleServices.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-[#0f172a] text-sm">Aanbevolen voor deze module</h2>
            <Link to="/platform/diensten" className="text-[#D4A017] text-xs hover:underline">Meer →</Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {visibleServices.map(svc => (
              <div key={svc.id} className="bg-[#f8fafc] rounded-xl p-4 flex items-start justify-between">
                <div>
                  <p className="text-[#0f172a] font-semibold text-xs">{svc.name}</p>
                  <p className="text-[#94a3b8] text-[10px] mt-0.5">{svc.desc}</p>
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  <p className="text-[#D4A017] font-bold text-sm">{svc.price}</p>
                  <button onClick={() => handleOrder(svc.id, svc.name)} disabled={ordering === svc.id}
                    className="text-[10px] font-bold text-white bg-[#0f172a] px-3 py-1.5 rounded-lg mt-1 hover:bg-[#1e293b] transition-all flex items-center gap-1">
                    <ShoppingCart size={9} /> Bestel
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {isManaged && services.length > 0 && (
        <div className="bg-[#0f172a] text-white rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-wider text-[#D4A017] font-bold mb-1">Managed</p>
          <p className="text-sm font-semibold">Geen losse pakketten nodig</p>
          <p className="text-xs text-white/60 mt-1">Uw Managed AI plan voert deze optimalisaties al binnen het abonnement uit waar technisch mogelijk en koppelingen het toestaan.</p>
        </div>
      )}
    </div>
  );
}
